/* eslint-disable react-doctor/prefer-tag-over-role -- ARIA combobox/listbox/option pattern requires these roles; no semantic HTML alternatives exist for portaled custom dropdowns */
/* eslint-disable react-doctor/no-redundant-roles -- role="combobox" is required on <input type="text"> for the ARIA combobox pattern; its implicit role is "textbox" */
'use client';

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search } from 'lucide-react';
import { useFilters } from '@/context/FiltersContext';
import { useMap } from '@/context/MapContext';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const getCountryName = (code: string): string => {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
};

interface DropdownPos { top: number; left: number; width: number }

const LISTBOX_ID = 'country-listbox';
const getOptionId = (i: number): string => `country-option-${i}`;

export const CountryFilter: React.FC = () => {
  const { filters, updateFilter } = useFilters();
  const { startups } = useMap();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const allCountries = useMemo(() => {
    const codes = [...new Set(startups.flatMap(s => s.country ? [s.country] : []))];
    return codes
      .map(code => ({ code, name: getCountryName(code) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [startups]);

  const filtered = useMemo(() => {
    if (!query) return allCountries;
    const q = query.toLowerCase();
    return allCountries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [allCountries, query]);

  const displayedOptions = filtered.slice(0, 40);

  const toggle = (code: string): void => {
    const next = new Set(filters.countries);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    updateFilter('countries', next);
  };

  const updateDropdownPos = (): void => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
  };

  const openDropdown = (): void => {
    setOpen(true);
    setActiveIndex(-1);
    updateDropdownPos();
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = (target as Element).closest?.('[data-country-dropdown]');
      if (!isInsideContainer && !isInsideDropdown) { setOpen(false); setActiveIndex(-1); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const reposition = (): void => updateDropdownPos();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openDropdown();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = Math.min(activeIndex + 1, displayedOptions.length - 1);
        setActiveIndex(next);
        document.getElementById(getOptionId(next))?.scrollIntoView({ block: 'nearest' });
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = Math.max(activeIndex - 1, -1);
        setActiveIndex(prev);
        if (prev >= 0) document.getElementById(getOptionId(prev))?.scrollIntoView({ block: 'nearest' });
        break;
      }
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < displayedOptions.length) {
          toggle(displayedOptions[activeIndex].code);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        break;
      case 'Tab':
        setOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  return (
    <div ref={containerRef}>
      <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-text-muted pb-[6px]">Country</div>

      {filters.countries.size > 0 && (
        <div className="flex flex-wrap gap-[4px] mb-[6px]">
          {[...filters.countries].map(code => (
            <button
              key={code}
              type="button"
              onClick={() => toggle(code)}
              className="flex items-center gap-[4px] text-[10px] font-medium px-[6px] py-[2px] rounded-sm border border-accent text-accent bg-bg-panel cursor-pointer tracking-[0.02em] hover:bg-bg-raised"
            >
              {getCountryName(code)} <X size={10} />
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <Search size={11} className="absolute left-[8px] top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
        <input
          ref={inputRef}
          role="combobox"
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); openDropdown(); }}
          onFocus={openDropdown}
          onKeyDown={handleKeyDown}
          placeholder="Search by country…"
          aria-label="Search by country"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={LISTBOX_ID}
          aria-activedescendant={activeIndex >= 0 ? getOptionId(activeIndex) : undefined}
          className="w-full bg-bg-raised border border-border text-text-secondary text-[11px] pl-[24px] pr-[8px] py-[5px] rounded-sm placeholder:text-text-muted focus:outline-none focus:border-border-mid tracking-[0.02em]"
        />
      </div>

      {open && displayedOptions.length > 0 && dropdownPos && createPortal(
        <div
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Countries"
          data-country-dropdown
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 'var(--z-dropdown)', backgroundColor: 'var(--bg-panel)' }}
          className="border border-border-mid rounded-sm max-h-[160px] overflow-y-auto shadow-lg"
        >
          {displayedOptions.map(({ code, name }, i) => (
            <button
              key={code}
              id={getOptionId(i)}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={filters.countries.has(code)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => { toggle(code); setOpen(false); setActiveIndex(-1); }}
              className={`w-full text-left text-[11px] px-[8px] py-[5px] cursor-pointer tracking-[0.02em] transition-colors ${
                i === activeIndex
                  ? 'bg-bg-raised text-text-primary'
                  : filters.countries.has(code)
                  ? 'bg-bg-panel text-accent'
                  : 'bg-bg-panel text-text-secondary hover:bg-bg-raised'
              }`}
            >
              {name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};
